import { Link } from 'react-router-dom'
import PublicLayout from '../components/layout/PublicLayout'

function Item({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)]/60 p-6 backdrop-blur-sm">
      <h2 className="font-display text-xl font-bold">{title}</h2>
      <div className="mt-2 space-y-2 leading-relaxed text-[var(--text-secondary)]">{children}</div>
    </div>
  )
}

export default function Security() {
  return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-6 py-16">
        <p className="font-display text-sm font-bold uppercase tracking-widest text-[var(--accent-teal)]">Security &amp; privacy</p>
        <h1 className="font-display mt-2 text-4xl font-extrabold leading-tight md:text-5xl">
          Plain talk about your data.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[var(--text-secondary)]">
          We'd rather be honest about how this works than hide behind marketing. Here's exactly what protects your log,
          and what doesn't.
        </p>

        <div className="mt-10 space-y-4">
          <Item title="Your key is your access">
            <p>
              Your log lives at a private URL made of random words. Anyone who has that link (and your password, if you
              set one) can open your log — so treat the key like a password: keep it private, and don't share the URL.
            </p>
          </Item>

          <Item title="Optional password lock">
            <p>
              You can add a password that locks the app on every visit or after a period of inactivity. Passwords are
              hashed on the server with PBKDF2 (never stored in plain text), verification is rate-limited per IP to slow
              guessing, and the stored hash is never sent back to the browser.
            </p>
            <p>
              Forgot it? If you've set a backup email, you can reset it via a single-use link — authorised by your email,
              never by the key alone (otherwise the password would be pointless).
            </p>
          </Item>

          <Item title="What we don't do">
            <p>
              No third-party analytics, no advertising, no trackers, and we never sell or share your data. There's no
              account profile to mine — we don't even require an email unless you opt into recovery.
            </p>
          </Item>

          <Item title="The honest limitation: no end-to-end encryption">
            <p>
              Your data is stored as plain JSON in Cloudflare KV. It is <strong>not</strong> end-to-end encrypted, which
              means a password protects the app's interface, not the underlying record — in principle the data could be
              read by the infrastructure that hosts it. We chose this trade-off to keep the app simple, fast, recoverable
              and free. Please don't store anything you'd consider highly sensitive.
            </p>
          </Item>

          <Item title="AI nutrition lookups">
            <p>
              When you log food, the text you type is sent to Anthropic's Claude API to produce the nutrition breakdown,
              then stored on your record. Only the food text is sent — not your identity or your wider log.
            </p>
          </Item>

          <Item title="Hosting, backups & recovery">
            <p>
              wlns.app runs on Cloudflare Pages with data in Cloudflare KV. Your last few versions are backed up
              automatically and restorable from Settings, and you can export everything as JSON at any time. A backup
              email lets you recover your key if you lose it.
            </p>
          </Item>

          <Item title="Staying safe — a short checklist">
            <ul className="list-disc space-y-1 pl-5">
              <li>Keep your key private</li>
              <li>Set a password if others use your device</li>
              <li>Add a backup email so you can recover access</li>
              <li>Export a JSON backup every now and then</li>
            </ul>
          </Item>
        </div>

        <p className="mt-10 text-sm text-[var(--text-secondary)]">
          Have a security question or found an issue?{' '}
          <Link to="/contact" className="text-[var(--accent-teal)] underline">Contact us</Link>.
        </p>
      </div>
    </PublicLayout>
  )
}
